import {Action, Module, Mutation} from 'vuex-module-decorators'
import store from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import StudyDefinitionDto = MdbsCore.StudyDefinitionDto;
import IStudyDefinitionCreateDto = MdbsCore.IStudyDefinitionCreateDto;
import IStudyDefinitionUpdateDto = MdbsCore.IStudyDefinitionUpdateDto;
import StudyDefinitionUpdateDto = MdbsCore.StudyDefinitionUpdateDto;
import StudyDefinitionCreateDto = MdbsCore.StudyDefinitionCreateDto;
import {OrderComparator} from "@/auxillary/sorting";
import DataFieldDefinitionDto = MdbsCore.DataFieldDefinitionDto;
import QueryTypeDefinitionDto = MdbsCore.QueryTypeDefinitionDto;
import {canCreateQuery, canSeeQueryDefinition} from "@/components/query/QueryPermissions";

const studyDefinitionClient = new MdbsCore.StudyDefinitionClient();

@Module({namespaced: true, name: 'studyDefinition', store: store})
export default class StudyDefinitionModule extends BaseModule {
    studyDefinition: StudyDefinitionDto = new StudyDefinitionDto();
    dataFieldDefinitions = new Array<DataFieldDefinitionDto>();

    get patientDefinition(){
        return this.studyDefinition?.patient;
    }

    get visitDefinitionById() {
        return (definitionId: string) => this.studyDefinition?.visits?.find(v => v.id == definitionId);
    }

    get instrumentDefinitionById() {
        return (definitionId: string) => {
            if (this.studyDefinition?.instruments == undefined)
                return undefined;
            const instrumentsLength = this.studyDefinition.instruments.length
            for (let i = 0; i < instrumentsLength; i++) {
                if (this.studyDefinition.instruments[i].id == definitionId)
                    return this.studyDefinition.instruments[i];
            }
            return undefined;
        }
    }

    get dataFieldDefinitionById() {
        return (definitionId: string) => this.dataFieldDefinitions.find(v => v.id == definitionId);
    }

    get queryTypeDefinitions(): Array<QueryTypeDefinitionDto> {
        return this.studyDefinition.queryTypes || [];
    }

    get myVisibleQueryTypeDefinitions(): Array<QueryTypeDefinitionDto> {
        return this.queryTypeDefinitions
            .filter(qt => !qt.isDeleted)
            .filter(qt => canSeeQueryDefinition(qt));
    }

    get myCreatableQueryTypeDefinitions(): Array<QueryTypeDefinitionDto> {
        return this.myVisibleQueryTypeDefinitions
            .filter(qt => canCreateQuery(qt));
    }

    @Mutation
    setStudyDefinition(studyDefinition: StudyDefinitionDto) {
        this.studyDefinition = studyDefinition;
        this.studyDefinition.visits?.sort(OrderComparator)
        this.studyDefinition.visits?.forEach(v => v.instruments?.sort(OrderComparator))
        this.studyDefinition.instruments?.sort(OrderComparator)
        this.studyDefinition.standaloneInstruments?.sort(OrderComparator)
        this.studyDefinition.enumValueSets?.forEach(e => e.values?.sort(OrderComparator))
        this.dataFieldDefinitions.clear();
        this.dataFieldDefinitions.push(...studyDefinition.patient?.fields ?? [])
        this.dataFieldDefinitions.push(...studyDefinition.instruments?.flatMap(i => i.fields ?? []) ?? [])
        this.dataFieldDefinitions.push(...studyDefinition.visits?.flatMap(i => i.fields ?? []) ?? [])
    }

    @Mutation
    reset() {
        this.studyDefinition = new StudyDefinitionDto();
        this.dataFieldDefinitions.clear();
    }

    @Action({rawError: true})
    async loadById(id: string) {
        await this.execute(async () => {
            if (this.studyDefinition.id != id) {
                // only load if id is different
                this.setStudyDefinition(await studyDefinitionClient.getById(id))
            }
        }).catch(() => this.reset());
    }

    @Action({rawError: true})
    async create(studyDefinition: IStudyDefinitionCreateDto) {
        await this.execute(async () => {
            const createdStudyDefinition = await studyDefinitionClient.create(new StudyDefinitionCreateDto(studyDefinition))
            this.setStudyDefinition(await studyDefinitionClient.getById(createdStudyDefinition.id))
        }).catch();
    }

    @Action({rawError: true})
    async update(studyDefinition: IStudyDefinitionUpdateDto) {
        await this.execute(async () => {
            await studyDefinitionClient.update(studyDefinition.id, new StudyDefinitionUpdateDto(studyDefinition));
            this.setStudyDefinition(await studyDefinitionClient.getById(studyDefinition.id))
        }).catch();
    }

    @Action({rawError: true})
    async delete(id: string) {
        await this.execute(async () => {
            await studyDefinitionClient.delete(id);
            this.reset()
        }).catch();
    }
}
